import React from 'react';
import { Link } from 'react-router-dom';
import { insights, faqs } from '../data/mock';
import { FileText, TrendingUp, ShieldCheck } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Insights = () => {
  const methodology = [
    {
      icon: <TrendingUp className="w-8 h-8 text-[#D4AF37]" />,
      title: 'Пазарен анализ',
      description: 'Детайлно проучване на текущата ситуация, исторически данни и прогнози за развитие.'
    },
    {
      icon: <ShieldCheck className="w-8 h-8 text-[#D4AF37]" />,
      title: 'Риск оценка',
      description: 'Идентифициране и анализ на всички потенциални рискове и начини за тяхното минимизиране.'
    },
    {
      icon: <FileText className="w-8 h-8 text-[#D4AF37]" />,
      title: 'Препоръки',
      description: 'Конкретни стъпки и сценарии, базирани на вашите цели и пазарните възможности.'
    }
  ];

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="container-luxury mb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-[#111111] border border-[#D4AF37]/20 rounded-full mb-8">
            <span className="text-sm text-[#D4AF37] font-medium tracking-wider">ИНФОРМИРАНИЯТ ИЗБОР</span>
          </div>
          
          <h1 className="heading-luxury mb-6">
            Вземете решения, базирани на <span className="gradient-text">факти и анализ</span>
          </h1>
          
          <p className="text-xl text-[#A9A9A9] leading-relaxed">
            Нашата методология превръща информацията в конкурентно предимство
          </p>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="container-luxury mb-24">
        <h2 className="text-3xl font-serif font-semibold text-[#F6F2E8] text-center mb-12">
          Нашата методология
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {methodology.map((item, index) => (
            <div 
              key={index}
              className="card-luxury text-center"
            >
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                {item.icon}
              </div>
              <h3 className="text-xl font-serif font-semibold text-[#F6F2E8] mb-4">
                {item.title}
              </h3>
              <p className="text-[#A9A9A9] leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Market Briefs Section */}
      <section className="container-luxury mb-24">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-serif font-semibold text-[#F6F2E8] mb-4">
            Deus Market Brief
          </h2>
          <p className="text-lg text-[#A9A9A9] max-w-2xl mx-auto">
            Актуални пазарни анализи и инвестиционни възможности
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {insights.map((insight, index) => (
            <div 
              key={index}
              className="card-luxury group"
            >
              <div className="flex items-start justify-between mb-4">
                <span className="text-sm text-[#D4AF37] font-medium">{insight.date}</span>
                <FileText className="w-5 h-5 text-[#D4AF37] opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              
              <h3 className="text-xl font-serif font-semibold text-[#F6F2E8] mb-4">
                {insight.title}
              </h3>
              
              <p className="text-[#A9A9A9] leading-relaxed mb-6">
                {insight.summary}
              </p>
              
              <Link 
                to="/contact"
                className="text-[#D4AF37] font-medium hover:text-[#F3D98B] transition-colors duration-300"
              >
                Поискай пълен анализ →
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="container-luxury">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-semibold text-[#F6F2E8] mb-4">
              Често задавани въпроси
            </h2>
            <p className="text-lg text-[#A9A9A9]">
              Отговори на най-честите въпроси относно нашите услуги
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-[#111111] border border-[#D4AF37]/10 rounded-lg px-6 hover:border-[#D4AF37]/30 transition-colors duration-300"
              >
                <AccordionTrigger className="text-left text-[#F6F2E8] hover:text-[#D4AF37] hover:no-underline py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#A9A9A9] leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container-luxury mt-24">
        <div className="bg-gradient-to-br from-[#111111] via-[#0B0B0B] to-[#111111] border border-[#D4AF37]/20 rounded-2xl p-12 text-center">
          <h2 className="text-3xl lg:text-4xl font-serif font-semibold text-[#F6F2E8] mb-6">
            Нуждаете се от детайлен анализ?
          </h2>
          <p className="text-lg text-[#A9A9A9] mb-8 max-w-2xl mx-auto">
            Свържете се с нас за персонализирана консултация и пазарен анализ
          </p>
          <Link to="/contact" className="btn-primary">
            Запази консултация
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Insights;
